import { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Fab,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { EXPENSE_CATEGORIES } from '@school-homestead/shared'
import type { ExpenseDTO, AddExpenseInput, ExpenseCategory } from '@school-homestead/shared'

const EMPTY_FORM: AddExpenseInput = {
  amount: 0,
  category: 'Other',
  vendor: '',
  description: '',
}

export default function ExpensesPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<AddExpenseInput>(EMPTY_FORM)

  const { data, isLoading, error } = useQuery({
    queryKey: ['expenses'],
    queryFn: async () => {
      const res = await api.get<ExpenseDTO[]>('/api/expenses')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  const addMutation = useMutation({
    mutationFn: async (input: AddExpenseInput) => {
      const res = await api.post<ExpenseDTO>('/api/expenses', input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      setOpen(false)
      setForm(EMPTY_FORM)
    },
  })

  function handleClose() {
    setOpen(false)
    setForm(EMPTY_FORM)
    addMutation.reset()
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Expenses
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Add Expense
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load expenses.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ border: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        No expenses recorded yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (data ?? []).map((expense) => (
                      <TableRow key={expense.id} hover>
                        <TableCell>
                          {new Date(expense.datePurchased).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip label={expense.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{expense.vendor ?? '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {expense.description ?? '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          ${expense.amount.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Mobile FAB */}
      <Tooltip title="Add Expense">
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, display: { xs: 'flex', sm: 'none' } }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Add Expense Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Add Expense</DialogTitle>
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Amount ($)"
            type="number"
            value={form.amount > 0 ? form.amount : ''}
            onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
          <TextField
            label="Category"
            select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value as ExpenseCategory })}
            fullWidth
          >
            {EXPENSE_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Vendor"
            value={form.vendor ?? ''}
            onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            fullWidth
          />
          <TextField
            label="Description"
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />

          {addMutation.isError && (
            <Alert severity="error">
              {(addMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={addMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => addMutation.mutate(form)}
            disabled={form.amount <= 0 || addMutation.isPending}
            loading={addMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
